import {DocumentPreview} from "@/components/ui/forms/DocumentPreview";
import {
  clearCreate,
  initialCreateVideo,
  setIsChanged,
  setTarget,
} from "@/redux/slices/videoSlices/createSlice";

import {useEffect, useRef, useState} from "react";

import {isEqual} from "@helpers";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {UploadImagePopup} from "@popups/uploadPopups/UploadImagePopup";
import {
  clearOptions as clearProductOptions,
  getCountries,
  getProductLine,
} from "@redux/slices/productSlices/optionsSlice";
import {
  clearOptions as clearScenarioOptions,
  getProducts,
} from "@redux/slices/scenarioSlices/optionsSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {ControlButton} from "@ui/buttons/ControlButton";
import {EditField} from "@ui/forms/EditField";
import {EditTitle} from "@ui/forms/EditTitle";
import {ViewField} from "@ui/forms/ViewField";
import {BorderArticle} from "@ui/general/BorderArticle";
import {PreviewCard} from "@ui/general/PreviewCard";
import {Spinner} from "@ui/general/Spinner";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";

import styles from "./styles.module.scss";

export const VideoCreatePage = () => {
  const {openPopup} = usePopup();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const {isMobile} = useSelector((state) => state.common.settings);
  const {target, status} = useSelector((state) => state.video.create);
  const {products} = useSelector((state) => state.scenario.options);
  const {productLine, countries} = useSelector(
    (state) => state.product.options,
  );

  const [videoFile, setVideoFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const videoRef = useRef();

  const updateVideoField = (fieldName, value) => {
    dispatch(setTarget({...target, [fieldName]: value}));
  };

  const handleClickRemovePhoto = () => {
    setPhotoFile(null);
    updateVideoField("cover", null);
  };

  const handleClickRemoveVideo = () => {
    setVideoFile(null);
    updateVideoField("video", null);
  };

  const handleClickUploadImage = () => {
    openPopup(<UploadImagePopup setPhotoFile={setPhotoFile} />);
  };

  // const handleClickUploadVideo = () => {
  // 	openPopup(<UploadImagePopup setPhotoFile={setVideoFile} />)
  // }

  const handleSelect = (selectedOption) => {
    const productId = selectedOption.id;
    const currentProductIds = target.product_ids || [];

    if (currentProductIds.includes(productId)) {
      updateVideoField(
        "product_ids",
        currentProductIds.filter((id) => id !== productId),
      );
    } else {
      updateVideoField("product_ids", [...currentProductIds, productId]);
    }
  };

  useEffect(() => {
    if (target && initialCreateVideo) {
      dispatch(setIsChanged(!isEqual(target, initialCreateVideo)));
    }
  }, [target, initialCreateVideo, dispatch]);

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getCountries());
    dispatch(getProductLine());

    return () => {
      dispatch(clearCreate());
      dispatch(clearProductOptions());
      dispatch(clearScenarioOptions());
    };
  }, [dispatch]);

  useEffect(() => {
    if (videoRef.current && isMobile) {
      videoRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [location]);

  return (
    <div
      ref={videoRef}
      className={styles.videoWrapper}
    >
      <BorderArticle>
        <div className={styles.video}>
          <div className={styles.videoHead}>
            <BackButton />
          </div>

          {status === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.videoContent}>
              <div className={styles.videoEditContent}>
                <div className={styles.videoContentHead}>
                  <EditTitle
                    label={"video-name"}
                    value={target?.name}
                    onChange={(e) => updateVideoField("name", e.target.value)}
                    placeholder={t("input.video_name_placeholder")}
                    handleClickSave={() => console.log("save")}
                    disabledButton={false}
                  />

                  <ViewField
                    label={`${t("input.video")}*`}
                    customContent={
                      <div className={"flex flex-col items-start gap-[16rem]"}>
                        {videoFile ||
                          (target?.video && (
                            <DocumentPreview
                              name={
                                videoFile?.name ||
                                target?.video_metadata?.filename
                              }
                              onClickCross={() => handleClickRemoveVideo()}
                            />
                          ))}

                        <ControlButton
                          color={"var(--violetLight)"}
                          txt={t("button.upload_video")}
                          onClick={() => handleClickUploadImage()}
                        />
                      </div>
                    }
                  />

                  <ViewField
                    label={t("input.cover")}
                    customContent={
                      <div className={"flex flex-col items-start gap-[16rem]"}>
                        {photoFile ||
                          (target?.cover && (
                            <DocumentPreview
                              name={
                                photoFile?.name ||
                                target?.cover_metadata?.filename
                              }
                              onClickCross={() => handleClickRemovePhoto()}
                            />
                          ))}

                        <ControlButton
                          color={"var(--violetLight)"}
                          txt={t("button.upload_image")}
                          onClick={() => handleClickUploadImage()}
                        />
                      </div>
                    }
                  />
                </div>

                <ViewField
                  label={t("input.preview")}
                  direction={"col"}
                  customContent={
                    <PreviewCard
                      name={target?.name}
                      mask={target?.mask}
                      image={
                        photoFile
                          ? URL.createObjectURL(photoFile)
                          : target?.cover || undefined
                      }
                    />
                  }
                />
              </div>

              <ul className={`${styles.videoContentBody} ${styles.videoBody}`}>
                <EditField
                  label={`${t("input.related_products")}*`}
                  width={500}
                  height={150}
                  searchPlaceholder={t("input.product_search")}
                  multiSelect
                  activeOption={[]}
                  options={products}
                  onSelect={(selectedOption) => handleSelect(selectedOption)}
                  type={"select"}
                />

                <EditField
                  label={`${t("input.product_line")}*`}
                  width={280}
                  height={150}
                  searchPlaceholder={t("input.product_line_search")}
                  activeOption={target?.product_line}
                  options={productLine}
                  onSelect={(selectedOption) =>
                    updateVideoField("product_line_id", selectedOption?.id)
                  }
                  type={"select"}
                />

                <EditField
                  label={`${t("input.country")}*`}
                  width={280}
                  height={150}
                  searchPlaceholder={t("input.country_search")}
                  activeOption={target?.country}
                  options={countries}
                  onSelect={(selectedOption) =>
                    updateVideoField("country_id", selectedOption?.id)
                  }
                  type={"select"}
                />

                <EditField
                  label={t("input.description")}
                  value={target?.description}
                  onChange={(e) =>
                    updateVideoField("description", e.target.value)
                  }
                  type={"textarea"}
                />
              </ul>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
